import { forwardRef, useRef, type InputHTMLAttributes } from 'react';
import { ClockIcon, CLOCK_ICON_SIZE_CLASS } from './ClockIcon';

export type NativeTimeInputProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> & {
  wrapperClassName?: string;
  iconClassName?: string;
};

const toHhMm = (raw: InputHTMLAttributes<HTMLInputElement>['value']): string => {
  if (raw == null || Array.isArray(raw)) {
    return '';
  }
  const text = String(raw).trim();
  if (text.length < 5) {
    return '';
  }
  return text.slice(0, 5);
};

export const NativeTimeInput = forwardRef<HTMLInputElement, NativeTimeInputProps>(
  (
    {
      className = '',
      wrapperClassName = '',
      iconClassName = CLOCK_ICON_SIZE_CLASS,
      disabled,
      step = 60,
      value,
      ...rest
    },
    forwardedRef,
  ) => {
    const innerRef = useRef<HTMLInputElement>(null);
    const overlayTime = toHhMm(value);

    const setInputRef = (node: HTMLInputElement | null) => {
      innerRef.current = node;
      if (typeof forwardedRef === 'function') {
        forwardedRef(node);
        return;
      }
      if (forwardedRef) {
        forwardedRef.current = node;
      }
    };

    const handleOpenPicker = () => {
      const inputEl = innerRef.current;
      if (!inputEl || disabled) return;
      if (typeof inputEl.showPicker === 'function') {
        try {
          inputEl.showPicker();
          return;
        } catch {
          // Fall through when showPicker is blocked by the browser.
        }
      }
      inputEl.focus();
      inputEl.click();
    };

    const handleOpenPickerKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        handleOpenPicker();
      }
    };

    return (
      <div className={['relative min-w-0', wrapperClassName].filter(Boolean).join(' ')}>
        <input
          ref={setInputRef}
          type="time"
          {...rest}
          lang="en-GB"
          step={step}
          disabled={disabled}
          value={value}
          className={['fdh-native-time-input pr-10 text-sm', className].filter(Boolean).join(' ')}
        />
        <span
          className={[
            'pointer-events-none absolute top-1/2 left-3 z-[1] -translate-y-1/2 text-sm tabular-nums',
            overlayTime ? 'text-gray-900 dark:text-gray-100' : 'text-gray-400 dark:text-gray-500',
            disabled ? 'opacity-50' : '',
          ]
            .filter(Boolean)
            .join(' ')}
          aria-hidden
        >
          {overlayTime || '--:--'}
        </span>
        <button
          type="button"
          tabIndex={-1}
          disabled={disabled}
          aria-hidden
          onClick={handleOpenPicker}
          onKeyDown={handleOpenPickerKeyDown}
          className="pointer-events-auto absolute top-1/2 right-2 z-[1] flex -translate-y-1/2 items-center justify-center disabled:cursor-not-allowed disabled:opacity-50"
        >
          <ClockIcon className={iconClassName} />
        </button>
      </div>
    );
  },
);

NativeTimeInput.displayName = 'NativeTimeInput';
